import React, { useCallback } from 'react'
import { SwapProvider, useSwapContext } from '@plug-core/providers/SwapProvider'
import useSwap from '@plug-core/hooks/useSwap'
import { useTranslation } from 'react-i18next'
import BottomSheet from '@components/BottomSheet'
import SwapReview from './Components/SwapReview'
import ExchangeView from './ExchangeView'
import ErrorPopup from '@components/ErrorPopup'
import { ROUTES } from '@utils/routes'
import { useLocation, useNavigate } from 'react-router-dom'
import Touchable from '@components/Touchable'
import { IconButton, Stack, Typography } from '@mui/material'
import SwapConfig from '@components/SwapConfig'
import { Titlebar } from '@components/Titlebar/Titlebar'
import { HiCog } from 'react-icons/hi'
import useConfig from '@plug-core/hooks/useConfig'
import { SwapProvider as Providers } from '@plug-core/interfaces/swapInterface'
import { Chain } from '@plug-core/interfaces/common'

const SwapTokenSelect = () => {
  const { t } = useTranslation()
  const { state } = useLocation()
  const navigate = useNavigate()

  const {
    fromToken,
    toToken,
    setFromToken,
    setToToken,
    amount,
    setAmount,
    toAmount,
    setToAmount,
    swapReviewVisible,
    setSwapReviewVisible,
    swapConfigVisible,
    setSwapConfigVisible,
    chainMode,
    setChainMode,
  } = useSwapContext()

  const {
    performSwap,
    currentSwapStep,
    currentQuote,
    swapError,
    swapExecutionError,
    setExecutionSwapError,
    priceHuman,
    maxAmount,
  } = useSwap({ fromTokenId: state?.fromTokenId, toTokenId: state?.toTokenId })

  const { tokens: allTokens } = useConfig()

  const onErrorClose = () => {
    setSwapReviewVisible(false)
    setExecutionSwapError(undefined)
  }

  const handleSubmit = useCallback(() => setSwapReviewVisible(true), [setSwapReviewVisible])

  const onError = (error: string) => {
    setSwapReviewVisible(false)
    setExecutionSwapError(error)
  }

  return (
    <>
      <Stack pt={2} pb={1} mx={1}>
        <Titlebar
          title={t('swap.title')}
          onActionClick={{
            type: 'close',
            action: () => navigate(-1),
          }}
          leftItem={
            <IconButton
              sx={{ width: 40 }}
              color="primary"
              onClick={() => setSwapConfigVisible(true)}
            >
              <HiCog />
            </IconButton>
          }
        />
      </Stack>
      <ExchangeView
        setFromToken={(id) => setFromToken(allTokens.find((t) => t.id === id))}
        toToken={toToken}
        setToToken={(id) => setToToken(allTokens.find((t) => t.id === id))}
        fromToken={fromToken}
        amount={amount}
        setAmount={setAmount}
        maxAmount={maxAmount}
        toAmount={toAmount}
        setToAmount={setToAmount}
        priceHuman={priceHuman}
        error={swapError}
        currentQuote={currentQuote}
        tokens={allTokens}
        destinationTokens={allTokens}
        nextTitle={t('swap.reviewSwap')}
        handleSubmit={handleSubmit}
        mode="swap"
        chainMode={chainMode}
        setChainMode={setChainMode}
        chains={[Chain.ICP, Chain.ODIN]}
      >
        <BottomSheet
          visible={swapReviewVisible}
          onBackdropClick={() => setSwapReviewVisible(false)}
        >
          <SwapReview performSwap={performSwap} currentStep={currentSwapStep} onError={onError} />
        </BottomSheet>
        {currentQuote?.pair.provider === Providers.ICPSWAP && (
          <Touchable onClick={() => navigate(ROUTES.SWAP_FALLBACK_PROVIDER_SELECT)}>
            <Typography
              color={(theme) => theme.palette.rainbow.blue}
              textAlign="center"
              fontWeight="bold"
              fontSize={14}
            >
              {t('swap.fallback')}
            </Typography>
          </Touchable>
        )}
        <SwapConfig visible={swapConfigVisible} close={() => setSwapConfigVisible(false)} />
      </ExchangeView>
      <ErrorPopup
        visible={!!swapExecutionError}
        title="Swap Failed"
        description={swapExecutionError?.toString()}
        onDismiss={onErrorClose}
      />
    </>
  )
}

export const Swap = () => (
  <SwapProvider>
    <SwapTokenSelect />
  </SwapProvider>
)
